import { CardGiftcard } from "@mui/icons-material";
import { Box, Chip, Stack, Typography } from "@mui/joy";
import { ApiCreditProductAdmin, ApiCreditSystemAdmin } from "griptonite-api-client/types";
import { ReactNode } from "react";

import getThumbnailUrl from "@/utils/getThumbnailUrl";
import pluralize from "@/utils/pluralize";

import { REWARD_CATEGORY_LABELS } from "./rewardCategories";
import { OfferedAt } from "./RewardsOverview";

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <Stack gap={0.25}>
    <Typography level="body-sm" textColor="neutral.500">
      {label}
    </Typography>
    {children}
  </Stack>
);

// A reward's details for staff, who can stock it but not edit it.
export default function RewardSummary({
  reward,
  system,
}: {
  reward: ApiCreditProductAdmin;
  system: ApiCreditSystemAdmin;
}) {
  return (
    <Stack gap={2} sx={{ p: 2, flex: 1, minHeight: 0, overflowY: "auto" }}>
      <Stack direction="row" gap={2} alignItems="flex-start">
        <Box
          sx={{
            width: 96,
            height: 96,
            flexShrink: 0,
            borderRadius: "md",
            overflow: "hidden",
            backgroundColor: "neutral.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "neutral.400",
          }}
        >
          {reward.image ? (
            <img
              alt=""
              src={getThumbnailUrl(reward.image.url)}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <CardGiftcard />
          )}
        </Box>
        <Stack gap={0.5} sx={{ minWidth: 0 }}>
          <Typography level="title-lg">{reward.name}</Typography>
          {reward.description ? (
            <Typography level="body-sm" sx={{ whiteSpace: "pre-line" }}>
              {reward.description}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
      <Field label="Category">
        <Typography>{REWARD_CATEGORY_LABELS[reward.category] ?? reward.category}</Typography>
      </Field>
      <Field label="Cost">
        <Typography>{pluralize(reward.cost, "credit")}</Typography>
      </Field>
      {system.venues.length > 1 ? (
        <Field label="Offered at">
          <OfferedAt venueUuids={reward.venues} system={system} />
        </Field>
      ) : null}
      <Field label="Status">
        <Chip
          variant="soft"
          color={reward.isActive ? "success" : "neutral"}
          sx={{ fontWeight: "bold", alignSelf: "flex-start" }}
        >
          {reward.isActive ? "Enabled" : "Disabled"}
        </Chip>
      </Field>
      <Typography level="body-sm" textColor="neutral.500">
        Only gym admins can change a reward’s details.
      </Typography>
    </Stack>
  );
}
